import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  InputAdornment,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import {
  Devices as DevicesIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Computer as ComputerIcon,
  PhoneAndroid as PhoneIcon,
  Tablet as TabletIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material';
import {
  getAllUserSessions,
  getUserSessionsByUser,
  terminateUserSession,
  terminateAllUserSessions,
  getSessionViolations,
  getRecentViolations,
} from '../../api/adminApi';
import { useToast } from '../../contexts/ToastContext';
import ModernButton from '../../components/common/ModernButton';
import ModernDataGrid from '../../components/common/ModernDataGrid';

const SessionsManagement = () => {
  const [sessions, setSessions] = useState([]);
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState('true');
  const [viewDialog, setViewDialog] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const { showToast } = useToast();

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (search) params.search = search;
      if (activeOnly !== '') params.isActive = activeOnly;

      const { data } = await getAllUserSessions(params);
      // Backend returns { success, sessions, total, page, limit, pages }
      setSessions(data.sessions || []);
      setTotalCount(data.total || data.sessions?.length || 0);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      showToast('Failed to fetch sessions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchViolations = async () => {
    setLoading(true);
    try {
      const { data } = await getRecentViolations({ limit: 100 });
      // Backend returns { success, violations }
      setViolations(data.violations || []);
    } catch (error) {
      console.error('Failed to fetch violations:', error);
      showToast('Failed to fetch violations', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 0) {
      fetchSessions();
    } else {
      fetchViolations();
    }
  }, [page, pageSize, activeOnly, tabValue]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchSessions();
  };

  const handleTerminateSession = async (sessionId) => {
    try {
      await terminateUserSession(sessionId);
      showToast('Session terminated successfully', 'success');
      setConfirmDialog(null);
      fetchSessions();
    } catch (error) {
      showToast('Failed to terminate session', 'error');
    }
  };

  const handleTerminateAllUserSessions = async (userId) => {
    try {
      await terminateAllUserSessions(userId);
      showToast('All user sessions terminated successfully', 'success');
      setConfirmDialog(null);
      fetchSessions();
    } catch (error) {
      showToast('Failed to terminate sessions', 'error');
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return <PhoneIcon fontSize="small" />;
      case 'tablet':
        return <TabletIcon fontSize="small" />;
      default:
        return <ComputerIcon fontSize="small" />;
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  const getTimeSince = (date) => {
    if (!date) return 'N/A';
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const sessionColumns = [
    {
      field: 'user',
      headerName: 'User',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
            {params.row.userId?.firstName?.[0] || '?'}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {params.row.userId?.firstName} {params.row.userId?.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.userId?.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'device',
      headerName: 'Device',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getDeviceIcon(params.row.device?.type)}
          <Box>
            <Typography variant="body2">
              {params.row.device?.browser || 'Unknown'} / {params.row.device?.os || 'Unknown'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.device?.type || 'desktop'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {params.row.location?.city || 'Unknown'}, {params.row.location?.country || params.row.location?.countryCode || '??'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.ipAddress || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.isActive ? 'Active' : 'Inactive'}
          color={params.row.isActive ? 'success' : 'default'}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'lastActiveAt',
      headerName: 'Last Activity',
      width: 130,
      renderCell: (params) => (
        <Tooltip title={formatDate(params.row.lastActiveAt)}>
          <Typography variant="body2">
            {getTimeSince(params.row.lastActiveAt)}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'loginAt',
      headerName: 'Login Time',
      width: 160,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => setViewDialog(params.row)}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Terminate Session">
            <IconButton
              size="small"
              color="error"
              onClick={() => setConfirmDialog({ type: 'session', data: params.row })}
              disabled={!params.row.isActive}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Terminate All User Sessions">
            <IconButton
              size="small"
              color="warning"
              onClick={() => setConfirmDialog({ type: 'user', data: params.row })}
            >
              <PersonOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const violationColumns = [
    {
      field: 'user',
      headerName: 'User',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'error.light' }}>
            {params.row.userId?.firstName?.[0] || '?'}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {params.row.userId?.firstName} {params.row.userId?.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.userId?.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'violationType',
      headerName: 'Violation Type',
      width: 180,
      renderCell: (params) => (
        <Chip
          icon={<WarningIcon />}
          label={params.row.violationType?.replace(/_/g, ' ') || 'Unknown'}
          color="error"
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'ipAddress',
      headerName: 'IP Address',
      width: 130,
    },
    {
      field: 'createdAt',
      headerName: 'Time',
      width: 180,
      valueFormatter: (params) => formatDate(params.value),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ backgroundColor: 'primary.main', width: 48, height: 48, mr: 2 }}>
              <DevicesIcon />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                Sessions Management
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Monitor and manage user login sessions
              </Typography>
            </Box>
          </Box>
          <ModernButton
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => tabValue === 0 ? fetchSessions() : fetchViolations()}
          >
            Refresh
          </ModernButton>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Active Sessions" />
          <Tab label="Violation Logs" />
        </Tabs>
      </Box>

      {/* Sessions Tab */}
      {tabValue === 0 && (
        <>
          {/* Filters */}
          <Card sx={{ mb: 3, p: 3, borderRadius: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <form onSubmit={handleSearch}>
                  <TextField
                    fullWidth
                    placeholder="Search by user name or email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </form>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={activeOnly}
                  onChange={(e) => setActiveOnly(e.target.value)}
                >
                  <MenuItem value="">All Sessions</MenuItem>
                  <MenuItem value="true">Active Only</MenuItem>
                  <MenuItem value="false">Inactive Only</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <ModernButton fullWidth variant="contained" onClick={fetchSessions}>
                  Filter
                </ModernButton>
              </Grid>
            </Grid>
          </Card>

          {/* Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Total Sessions</Typography>
                <Typography variant="h4" fontWeight={600}>{totalCount}</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Active Sessions</Typography>
                <Typography variant="h4" fontWeight={600} color="success.main">
                  {sessions.filter(s => s.isActive).length}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Unique Users</Typography>
                <Typography variant="h4" fontWeight={600} color="primary.main">
                  {new Set(sessions.map(s => s.userId?._id)).size}
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Data Grid */}
          <ModernDataGrid
            rows={sessions}
            columns={sessionColumns}
            loading={loading}
            getRowId={(row) => row._id}
            pagination
            page={page - 1}
            onPageChange={(newPage) => setPage(newPage + 1)}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            rowsPerPageOptions={[20, 50, 100]}
            rowCount={totalCount}
            paginationMode="server"
          />
        </>
      )}

      {/* Violations Tab */}
      {tabValue === 1 && (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            Violation logs track suspicious activities such as exceeded session limits,
            concurrent exam attempts, and forced session terminations.
          </Alert>

          <ModernDataGrid
            rows={violations}
            columns={violationColumns}
            loading={loading}
            getRowId={(row) => row._id}
            pageSize={100}
            rowsPerPageOptions={[50, 100]}
          />
        </>
      )}

      {/* View Session Dialog */}
      {viewDialog && (
        <Dialog open={!!viewDialog} onClose={() => setViewDialog(null)} maxWidth="md" fullWidth>
          <DialogTitle>Session Details</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      User Information
                    </Typography>
                    <Typography variant="body1">
                      <strong>Name:</strong> {viewDialog.userId?.firstName} {viewDialog.userId?.lastName}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Email:</strong> {viewDialog.userId?.email}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Device Information
                    </Typography>
                    <Typography variant="body1">
                      <strong>Browser:</strong> {viewDialog.device?.browser || 'Unknown'} {viewDialog.device?.browserVersion || ''}
                    </Typography>
                    <Typography variant="body1">
                      <strong>OS:</strong> {viewDialog.device?.os || 'Unknown'} {viewDialog.device?.osVersion || ''}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Device Type:</strong> {viewDialog.device?.type || 'desktop'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Location
                    </Typography>
                    <Typography variant="body1">
                      <strong>IP Address:</strong> {viewDialog.ipAddress || 'N/A'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>City:</strong> {viewDialog.location?.city || 'Unknown'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Country:</strong> {viewDialog.location?.country || viewDialog.location?.countryCode || 'Unknown'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Timezone:</strong> {viewDialog.location?.timezone || 'Unknown'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Session Timing
                    </Typography>
                    <Typography variant="body1">
                      <strong>Login Time:</strong> {formatDate(viewDialog.loginAt)}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Last Activity:</strong> {formatDate(viewDialog.lastActiveAt)}
                    </Typography>
                    {viewDialog.logoutAt && (
                      <Typography variant="body1">
                        <strong>Logout Time:</strong> {formatDate(viewDialog.logoutAt)}
                      </Typography>
                    )}
                    {viewDialog.logoutReason && (
                      <Typography variant="body1">
                        <strong>Logout Reason:</strong> {viewDialog.logoutReason}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Technical Details
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      <strong>Session ID:</strong> {viewDialog._id}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      <strong>User Agent:</strong> {viewDialog.device?.userAgent || 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setViewDialog(null)}>Close</ModernButton>
            {viewDialog.isActive && (
              <ModernButton
                variant="contained"
                color="error"
                onClick={() => {
                  setViewDialog(null);
                  setConfirmDialog({ type: 'session', data: viewDialog });
                }}
              >
                Terminate Session
              </ModernButton>
            )}
          </DialogActions>
        </Dialog>
      )}

      {/* Confirm Dialog */}
      {confirmDialog && (
        <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)} maxWidth="sm">
          <DialogTitle>
            {confirmDialog.type === 'session' ? 'Terminate Session' : 'Terminate All User Sessions'}
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {confirmDialog.type === 'session'
                ? 'This will immediately log out the user from this device. They will need to login again.'
                : `This will terminate ALL active sessions for ${confirmDialog.data.userId?.firstName} ${confirmDialog.data.userId?.lastName}. The user will be logged out from all devices.`
              }
            </Alert>
            <Typography variant="body2" color="text.secondary">
              User: {confirmDialog.data.userId?.firstName} {confirmDialog.data.userId?.lastName}
            </Typography>
            {confirmDialog.type === 'session' && (
              <Typography variant="body2" color="text.secondary">
                Device: {confirmDialog.data.device?.browser || 'Unknown'} / {confirmDialog.data.device?.os || 'Unknown'}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setConfirmDialog(null)}>Cancel</ModernButton>
            <ModernButton
              variant="contained"
              color="error"
              onClick={() => {
                if (confirmDialog.type === 'session') {
                  handleTerminateSession(confirmDialog.data._id);
                } else {
                  handleTerminateAllUserSessions(confirmDialog.data.userId?._id);
                }
              }}
            >
              {confirmDialog.type === 'session' ? 'Terminate Session' : 'Terminate All Sessions'}
            </ModernButton>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default SessionsManagement;
